import React, { useRef, useState, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import { IconButton } from './Button';
import { Icon } from './Icon';
import { iconWeight } from './primitives/tokens';
import styles from './ExpandableSearch.module.css';

const expandableSearch = cva(styles.base);

const heightBySize: Record<string, string> = {
  sm: 'var(--control-height-sm)',
  md: 'var(--control-height-md)',
  lg: 'var(--control-height-lg)',
};

export interface ExpandableSearchProps {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  /**
   * Label text used as the accessible name (`aria-label`) on the collapsed
   * search button AND the expanded input. The visible affordance is the
   * search icon, so a floating label would have nowhere to live — `label`
   * keeps screen-reader parity with the rest of the input family. The legacy
   * `placeholder` prop is intentionally removed.
   */
  label?: string;
  /** Size of the collapsed icon button */
  buttonSize?: 'sm' | 'md' | 'lg';
  /** Width when expanded (default 256) */
  expandedWidth?: number;
  /** Elevated style — surface bg, subtle border, soft shadow */
  elevated?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function ExpandableSearch({
  value,
  onChange,
  onClear,
  label = 'Search',
  buttonSize = 'md',
  expandedWidth = 256,
  elevated,
  className,
  style,
}: ExpandableSearchProps) {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const expand = useCallback(() => {
    setExpanded(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const handleBlur = useCallback(() => {
    if (!value) {
      setExpanded(false);
    }
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  if (!expanded) {
    return (
      <IconButton aria-label={label} size={buttonSize} elevated={elevated} onClick={expand} className={className} style={style}>
        <Icon name="search" weight={iconWeight.light} size={18} />
      </IconButton>
    );
  }

  return (
    <div
      className={expandableSearch({ className })}
      style={{
        '--expandable-height': heightBySize[buttonSize],
        '--expandable-width': `${expandedWidth}px`,
        ...style,
      } as React.CSSProperties}
    >
      <Icon
        name="search"
        weight={iconWeight.light}
        size={16}
        className={styles.searchIcon}
      />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-label={label}
        className={styles.input}
      />
      {value && (
        <IconButton aria-label="Clear" onClick={onClear} size="sm">
          <Icon name="close" weight={iconWeight.light} size={14} />
        </IconButton>
      )}
    </div>
  );
}
